/**
 * The four ways a price series can be drawn, defined once.
 *
 * ## Why this is not just a string passed to two renderers
 *
 * The web draws with Lightweight Charts and the phone draws with Skia. They
 * share no code and cannot: one configures a library, the other issues paths.
 * What they must share is the MEANING — a hollow candle has to be hollow in the
 * same direction on both, or a trader moving between a monitor and a phone sees
 * two different markets and has to work out which one is lying.
 *
 * So the meaning lives here as data, and each renderer reads it. Adding a fifth
 * style is a row in this file plus a branch in each renderer, and the two
 * branches cannot disagree about what the style IS.
 *
 * ## Hollow means hollow on the up side only
 *
 * The convention every platform uses, and it is worth stating because the
 * obvious alternative — outline everything — produces a chart where direction
 * is carried by hue alone. That is the thing `semantic.css` refuses to do for
 * P&L, and it would be no better here. Hollow keeps the down bars filled, so
 * direction survives with no colour perception at all.
 */
export type ChartStyle = 'candles' | 'hollow' | 'bars' | 'line';

/** Every style, in the order a picker should offer them. */
export const CHART_STYLES: readonly ChartStyle[] = ['candles', 'hollow', 'bars', 'line'];

export const DEFAULT_CHART_STYLE: ChartStyle = 'candles';

export interface ChartStyleShape {
  readonly id: ChartStyle;
  readonly label: string;
  /**
   * The primitive the renderer reaches for.
   *
   * `candle` has a body between open and close. `bar` has no body — a vertical
   * high-low line with an open tick left and a close tick right. `line` joins
   * closes and discards the other three prices from the DRAWING, though never
   * from the data behind it.
   */
  readonly kind: 'candle' | 'bar' | 'line';
  /** Whether a rising body is filled. False is what makes hollow hollow. */
  readonly fillUp: boolean;
  /** Whether a falling body is filled. True on every style that has bodies. */
  readonly fillDown: boolean;
  /**
   * Whether the drawing shows the open at all.
   *
   * False for `line`, and the crosshair readout is where that matters: a phone
   * has no hover, so the read-out is the only way to recover a price the line
   * did not draw. It still reports all four, because the bar still has them —
   * this flag exists so a caller can SAY that the drawing is a summary.
   */
  readonly showsOpen: boolean;
}

const SHAPES: Record<ChartStyle, ChartStyleShape> = {
  candles: {
    id: 'candles', label: 'Candles', kind: 'candle',
    fillUp: true, fillDown: true, showsOpen: true,
  },
  hollow: {
    id: 'hollow', label: 'Hollow', kind: 'candle',
    fillUp: false, fillDown: true, showsOpen: true,
  },
  bars: {
    id: 'bars', label: 'Bars', kind: 'bar',
    // No bodies at all, so neither flag is read. Recorded as false rather than
    // left undefined, because a renderer that reads them anyway should get the
    // answer that draws nothing rather than one that draws a phantom body.
    fillUp: false, fillDown: false, showsOpen: true,
  },
  line: {
    id: 'line', label: 'Line', kind: 'line',
    fillUp: false, fillDown: false, showsOpen: false,
  },
};

/**
 * The shape for a style, tolerating anything.
 *
 * A stored preference outlives the code that wrote it: a row written by a build
 * that offered five styles is still in the database when a build that offers
 * four reads it. Falling back to candles is right — an unknown style must not
 * blank the chart, and candles is what every trader can read.
 */
export function chartStyleShape(style: string | null | undefined): ChartStyleShape {
  return isChartStyle(style) ? SHAPES[style] : SHAPES[DEFAULT_CHART_STYLE];
}

/**
 * True when `style` is one this build knows how to draw.
 *
 * `Object.hasOwn`, not `in`. A plain object inherits from `Object.prototype`,
 * so `'toString' in SHAPES` is TRUE and `SHAPES['toString']` is a function —
 * which sailed past a `??` fallback and handed the renderer a shape whose
 * `.kind` was undefined. The stored value is a string from a database and is
 * not required to be one of ours.
 */
export function isChartStyle(style: string | null | undefined): style is ChartStyle {
  return typeof style === 'string' && Object.hasOwn(SHAPES, style);
}
